import { z } from "zod";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { CommandStatus, CommandType } from "../types/command.types";
import { queryClient as qc } from "../main";

const OrdersKanbanScope = ["secretariat", "modeling", "lab", "global"] as const;
export type OrdersKanbanScopeType = (typeof OrdersKanbanScope)[number];

const OrdersKanbanMaterial = ["SILICONE", "RESIN"] as const;
export type OrdersKanbanMaterialType = (typeof OrdersKanbanMaterial)[number];

const OrdersKanbanContextSchema = z.object({
	scope: z.enum(OrdersKanbanScope),
	filters: z.object({
		centerId: z.string().optional(),
		modelerId: z.string().optional(),
		status: z.string().optional(),
		notStatus: z.string().optional(),
		material: z.enum(OrdersKanbanMaterial).optional(),
		halted: z.boolean().optional(),
		type: z
			.enum([
				CommandType.OTOSCAN,
				CommandType.POSTE,
				CommandType.REWORK,
				CommandType.SUPPLY,
				CommandType.THREED,
			])
			.optional(),
	}),
	search: z.string().optional(),
	tab: z.enum(["table", "kanban"]).default("table"),
});
export type OrdersKanbanState = z.infer<typeof OrdersKanbanContextSchema>;

interface OrdersKanbanActions {
	setScope: (scope: OrdersKanbanScopeType) => void;
	getScopeColumns: () => CommandStatus[];
	setFilterCenterId: (id?: string) => void;
	setFilterModelerId: (id: string) => void;
	setFilterType: (type: CommandType) => void;
	setFilterMaterial: (material: OrdersKanbanMaterialType) => void;
	setFilterHalted: (halted?: boolean) => void;
	setFilterStatus: (status?: CommandStatus) => void;
	setFilterNotStatus: (status?: CommandStatus) => void;
	setSearch: (search: string | undefined) => void;
	hasFilters: () => boolean;
	resetFilters: () => void;
	resetSessionState: () => void;
	setTab: (tab: OrdersKanbanState["tab"]) => void;
}

const getInitialState = (): OrdersKanbanState => ({
	scope: "global",
	filters: {
		centerId: undefined,
		modelerId: undefined,
		type: undefined,
		material: undefined,
		halted: undefined,
		status: undefined,
		notStatus: undefined,
	},
	search: undefined,
	tab: "table",
});

const initialState: OrdersKanbanState = getInitialState();

export const useOrdersKanbanStore = create<
	OrdersKanbanState & OrdersKanbanActions
>()(
	devtools(
		persist(
			(set, get) => ({
				...initialState,
				setScope: (scope) => set({ scope }),
				getScopeColumns: () => {
					switch (get().scope) {
						case "secretariat":
							return [
								CommandStatus.WAITING_FOR_RECEPTION,
								CommandStatus.RECEIVED,
								CommandStatus.WAITING_FOR_MODELING,
								CommandStatus.READY_FOR_DELIVERY,
							];
						case "modeling":
							return [
								CommandStatus.RECEIVED,
								CommandStatus.WAITING_FOR_MODELING,
								CommandStatus.MODELING,
								CommandStatus.WAITING_FOR_PRINTING,
								CommandStatus.PRINTING,
								CommandStatus.PRINTED,
								CommandStatus.WAITING_FOR_LAB,
							];
						case "lab":
							return [
								CommandStatus.WAITING_FOR_LAB,
								CommandStatus.INJECTING,
								CommandStatus.POLISHING,
								CommandStatus.QUALITY_CONTROL,
								CommandStatus.READY,
								CommandStatus.READY_FOR_DELIVERY,
							];
						case "global":
							return [
								CommandStatus.WAITING_FOR_RECEPTION,
								CommandStatus.RECEIVED,
								CommandStatus.WAITING_FOR_MODELING,
								CommandStatus.MODELING,
								CommandStatus.WAITING_FOR_PRINTING,
								CommandStatus.PRINTING,
								CommandStatus.PRINTED,
								CommandStatus.WAITING_FOR_LAB,
								CommandStatus.INJECTING,
								CommandStatus.POLISHING,
								CommandStatus.QUALITY_CONTROL,
								CommandStatus.READY,
								CommandStatus.READY_FOR_DELIVERY,
							];
					}
				},
				setFilterCenterId: (id?: string) => {
					const filters = get().filters;

					if (typeof id !== "string" || id.trim() === "") {
						set({ filters: { ...filters, centerId: undefined } });
					} else {
						set({ filters: { ...filters, centerId: id } });
					}
					qc.invalidateQueries("commands");
					qc.invalidateQueries("commands-paginated");
				},
				setFilterModelerId: (id: string) => {
					const filters = get().filters;

					if (typeof id !== "string" || id.trim() === "") {
						set({ filters: { ...filters, modelerId: undefined } });
					} else {
						set({ filters: { ...filters, modelerId: id } });
					}
					qc.invalidateQueries("commands");
					qc.invalidateQueries("commands-paginated");
				},
				setFilterType: (type: CommandType) => {
					const filters = get().filters;

					if (typeof type !== "string" || type.trim() === "") {
						set({ filters: { ...filters, type: undefined } });
					} else {
						set({ filters: { ...filters, type } });
					}
					qc.invalidateQueries("commands");
					qc.invalidateQueries("commands-paginated");
				},
				setFilterHalted: (halted?: boolean) => {
					const filters = get().filters;

					set({ filters: { ...filters, halted } });
					qc.invalidateQueries("commands");
					qc.invalidateQueries("commands-paginated");
				},
				setFilterMaterial: (material: OrdersKanbanMaterialType) => {
					const filters = get().filters;

					if (typeof material !== "string" || material.trim() === "") {
						set({ filters: { ...filters, material: undefined } });
					} else {
						set({ filters: { ...filters, material } });
					}
					qc.invalidateQueries("commands");
					qc.invalidateQueries("commands-paginated");
				},
				setSearch: (search: string | undefined) => {
					if (typeof search !== "string" || search.trim() === "") {
						set({ search: undefined });
					} else {
						set({ search });
					}
					qc.invalidateQueries("commands");
					qc.invalidateQueries("commands-paginated");
				},
				hasFilters: () => {
					return [
						get().filters.centerId,
						get().filters.modelerId,
						get().filters.type,
						get().filters.material,
						get().filters.halted,
						get().filters.status,
						get().filters.notStatus,
						get().search,
					].some((filter) => filter !== undefined && filter !== null);
				},
				resetFilters: () => {
					set({
						filters: initialState.filters,
						search: initialState.search,
					});
					qc.invalidateQueries("commands");
					qc.invalidateQueries("commands-paginated");
				},
				resetSessionState: () => {
					set(getInitialState());
				},
				setFilterStatus: (status?: CommandStatus) => {
					set({ filters: { ...get().filters, status } });
					qc.invalidateQueries("commands");
					qc.invalidateQueries("commands-paginated");
				},
				setFilterNotStatus: (status?: CommandStatus) => {
					set({ filters: { ...get().filters, notStatus: status } });
					qc.invalidateQueries("commands");
					qc.invalidateQueries("commands-paginated");
				},
				setTab: (tab: OrdersKanbanState["tab"]) => {
					set({ tab });
				},
			}),
			{
				name: "order-kanban-store",
				version: 1,
				getStorage: () => localStorage,
			},
		),
	),
);
